import React, { useEffect, useMemo, useCallback, useState, ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssignmentIcon from '@mui/icons-material/Assignment';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
  Avatar,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Tooltip,
  Modal,
  Fade
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Delete as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  AccessTime as HoursIcon,
  AttachMoney as MoneyIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { Invoice, Shift, TableInvoice } from '../../../../types/ndisSection/invoiceTypes';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'app/store';
import {
  fetchInvoice,
  generateInvoice,
  deleteInvoice,
  clearError,
  fetchSingleInvoice,
  clearInvoices,
  clearGeneratedInvoices
} from 'slices/NdisSlices/finance/InvoiceSlice';
import { mapToTableInvoice } from './FinanceTab';
import * as XLSX from 'xlsx';
import WorkloadReport from './WorkLoadReport';
import { fetchParticipantsList } from 'slices/HomeTilesSlices';


// formatting dates in dropdown
function getLast5PlusCurrentFortnights() {
  const today = new Date();
  const year = today.getFullYear();

  const fortnights = [];
  let start = new Date(year, 0, 1);
  let count = 1;

  // Generate all fortnights for the year
  while (start.getFullYear() === year) {
    let end = new Date(start);
    end.setDate(end.getDate() + 13);

    if (end.getFullYear() !== year) {
      end = new Date(year, 11, 31);
    }

    fortnights.push({
      fortnight: count,
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });

    start = new Date(end);
    start.setDate(start.getDate() + 1);
    count++;
  }

  // Find current fortnight
  const currentIndex = fortnights.findIndex(f => {
    const s = new Date(f.start);
    const e = new Date(f.end);
    return today >= s && today <= e;
  });

  // Return last 5 + current
  const startIndex = Math.max(0, currentIndex - 5);
  return fortnights.slice(startIndex, currentIndex + 1);
}

const fortnights2025 = getLast5PlusCurrentFortnights();

// Predefined fortnights for 2025
// const fortnights2025 = [
//   { fortnight: 1, start: '2025-01-01', end: '2025-01-14' },
//   { fortnight: 2, start: '2025-01-15', end: '2025-01-28' },
//   { fortnight: 3, start: '2025-01-29', end: '2025-02-11' },
//   { fortnight: 4, start: '2025-02-12', end: '2025-02-25' },
//   { fortnight: 5, start: '2025-02-26', end: '2025-03-10' },
//   { fortnight: 6, start: '2025-03-11', end: '2025-03-24' },
//   { fortnight: 7, start: '2025-03-25', end: '2025-04-07' },
//   { fortnight: 8, start: '2025-04-08', end: '2025-04-21' },
//   { fortnight: 9, start: '2025-04-22', end: '2025-05-05' },
//   { fortnight: 10, start: '2025-05-06', end: '2025-05-19' },
//   { fortnight: 11, start: '2025-05-20', end: '2025-06-02' },
//   { fortnight: 12, start: '2025-06-03', end: '2025-06-16' },
//   { fortnight: 13, start: '2025-06-17', end: '2025-06-30' },
//   { fortnight: 14, start: '2025-07-01', end: '2025-07-14' },
//   { fortnight: 15, start: '2025-07-15', end: '2025-07-28' },
//   { fortnight: 16, start: '2025-07-29', end: '2025-08-11' },
//   { fortnight: 17, start: '2025-08-12', end: '2025-08-25' },
//   { fortnight: 18, start: '2025-08-26', end: '2025-09-08' },
//   { fortnight: 19, start: '2025-09-09', end: '2025-09-22' },
//   { fortnight: 20, start: '2025-09-23', end: '2025-10-06' },
//   { fortnight: 21, start: '2025-10-07', end: '2025-10-20' },
//   { fortnight: 22, start: '2025-10-21', end: '2025-11-03' },
//   { fortnight: 23, start: '2025-11-04', end: '2025-11-17' },
//   { fortnight: 24, start: '2025-11-18', end: '2025-12-01' },
//   { fortnight: 25, start: '2025-12-02', end: '2025-12-15' },
//   { fortnight: 26, start: '2025-12-16', end: '2025-12-29' },
//   { extra: true, start: '2025-12-30', end: '2025-12-31' }
// ];

// Interface for export row
interface ExportInvoiceRow {
  RegistrationNumber: string;
  NDISNumber: string;
  SupportsDeliveredFrom: string;
  SupportsDeliveredTo: string;
  SupportNumber: string;
  ClaimReference: string;
  Quantity: string;
  Hours: string;
  UnitPrice: string;
  GSTCode: string;
  AuthorisedBy: string;
  ParticipantApproved: string;
  InKindFundingProgram: string;
  ClaimType: string;
  CancellationReason: string;
  'ABN of Support Provider': string;
  InvoiceNumber: string;
}

interface InvoiceListProps {
  onView: (invoice: Invoice) => void;
  onEdit: (invoice: Invoice) => void;
  onPrint: (invoice: Invoice) => void;
  onDelete: (invoice: Invoice) => void;
  onCreate: () => void;
  isSmallScreen: boolean;
  setFortnightDate: React.Dispatch<React.SetStateAction<string>>;
}

interface Client {
  label: string;
  id: number;
  email: string;
}

const InvoiceList: React.FC<InvoiceListProps> = ({
  onView,
  onEdit,
  onPrint,
  onDelete,
  onCreate,
  isSmallScreen,
  setFortnightDate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch<AppDispatch>();
  const {
    invoices = [],
    generatedInvoices = [],
    loading,
    error
  } = useSelector((state: RootState) => state.invoice || {});

  const { pathname } = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'previous' ? 'previous' : 'current';
  const [tabValue, setTabValue] = useState<'current' | 'previous'>(initialTab);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [fortnight, setFortnight] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('info');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const selectedProjectId = useSelector(
    (state: RootState) => (state as any).homeTilesServices?.selectedProjectId || ''
  );
  const selectedServiceTitle = useSelector(
    (state: RootState) => state.serviceSelected.selectedServiceTitle || ''
  );
  // const participantsList = useSelector(
  //   (state: RootState) => state?.homeTilesServices?.participants
  // ) || { participants_list: [] };

  const parsedUserDetails = JSON.parse(localStorage.getItem('user-details') || '{}');
  const parsedSelectedClient = JSON.parse(localStorage.getItem('selectedClient') || '{}');

  const email = parsedUserDetails?.email || '';
  const role = parsedUserDetails?.role ? [parsedUserDetails.role] : [''];

  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
    projectId?: string;
    projectName?: string;
    isAdmin?: boolean;
  };

  const isAdminLogin =
    selectedProject?.isAdmin === true ||
    role?.some((r: any) => r?.label?.toLowerCase() === 'admin');

  console.log('isAdminLogin', isAdminLogin);

  // Fetch clients
  useEffect(() => {
    if (selectedProjectId && selectedServiceTitle && email && role.length > 0) {
      dispatch(
        fetchParticipantsList({
          group_id: selectedProjectId,
          service_name_id: selectedServiceTitle,
          nested_component_id: pathname,
          email: isAdminLogin ? '' : email,
          roles: role,
          client_name: ''
        })
      ).unwrap();
    }
  }, [selectedProjectId, selectedServiceTitle]);

  const participantsList = useSelector(
    (state: RootState) => state?.homeTilesServices?.participants
  ) || { participants_list: [] };

  // Prepare clients list
  const clientsList = useMemo(
    () =>
      participantsList.participants_list?.map(
        (participant: { name: string; email: string }, index: number) => ({
          label: participant.name || `Client ${index + 1}`,
          id: index + 1,
          email: participant.email
        })
      ) || [],
    [participantsList.participants_list]
  );

  // Format fortnights for dropdown
  const fortnights = useMemo(() => {
    return fortnights2025.map((fn) => {
      const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
          .toString()
          .padStart(2, '0')}/${date.getFullYear()}`;
      };

      const label = fn.fortnight
        ? `${fn.fortnight}: ${formatDate(fn.start)} - ${formatDate(fn.end)}`
        : `Extra: ${formatDate(fn.start)} - ${formatDate(fn.end)}`;

      return {
        value: label,
        startDate: new Date(fn.start),
        endDate: new Date(fn.end)
      };
    });
  }, []);

  // Set default fortnight to the 11th one (current fortnight)
  // useEffect(() => {
  //   if (fortnights.length > 0 && !fortnight) {
  //     const currentFortnight = fortnights.find(
  //       (f) =>
  //         new Date('2025-05-20') >= new Date(f.startDate) &&
  //         new Date('2025-05-20') <= new Date(f.endDate)
  //     );
  //     setFortnight(currentFortnight?.value || fortnights[10]?.value || '');
  //   }
  // }, [fortnights, fortnight]);

  useEffect(() => {
  if (fortnights.length > 0 && !fortnight) {
    const today = new Date();

    const currentFortnight = fortnights.find(
      (f) => today >= f.startDate && today <= f.endDate
    );

    setFortnight(currentFortnight?.value || fortnights[fortnights.length - 1]?.value || '');
  }
}, [fortnights, fortnight]);

  // Debug state changes
  useEffect(() => {
    console.log('Redux State - generatedInvoices:', generatedInvoices);
    console.log('Redux State - invoices:', invoices);
    console.log('Clients List:', clientsList);
    console.log('Tab Value:', tabValue);
  }, [generatedInvoices, invoices, clientsList, tabValue]);

  const uiInvoices: TableInvoice[] = useMemo(() => {
    console.log('--- Computing uiInvoices ---');
    console.log('Tab:', tabValue);
    console.log('Generated Invoices:', generatedInvoices);
    console.log('Previous Invoices:', invoices);
    console.log('Search Query:', searchQuery);
    console.log('Selected Clients:', selectedClients);

    const sourceInvoices = tabValue === 'current' ? generatedInvoices : invoices;
    console.log('Source_Invoices:', sourceInvoices);

    if (!sourceInvoices || sourceInvoices.length === 0) {
      console.log('No source invoices available.');
      return [];
    }

    const validInvoices = sourceInvoices.filter((invoice): invoice is Invoice => {
      const isValid = !!invoice && !!invoice.invoice_number;
      if (!isValid) console.warn('Invalid invoice:', invoice);
      return isValid;
    });
    console.log('Valid Invoices:', validInvoices);

    const mappedInvoices = validInvoices.map((invoice) => {
      const mapped = mapToTableInvoice({
        ...invoice,
        id: invoice.id || invoice.invoice_number || `temp-${Math.random()}`
      });
      console.log('Mapped Invoice:', mapped);
      return {
        ...mapped,
        invoiceNumber: mapped.invoiceNumber || 'N/A',
        customerName: mapped.customerName || 'Unknown',
        date: mapped.date || invoice.issued_date || '',
        totalHours: mapped.totalHours || invoice.summary?.total_hours || 0,
        totalAmount: mapped.totalAmount || invoice.summary?.total_amount_due || 0
      };
    });
    console.log('Mapped Invoices:', mappedInvoices);

  console.log('Filtering Invoices...');
  console.log('SearchQuery:', searchQuery);
  console.log('Selected Clients:', selectedClients);

const filteredInvoices = mappedInvoices.filter((invoice) => {
  const customerName = invoice.client_key || '';
  const invoiceNumber = invoice.invoiceNumber || '';
  const search = searchQuery?.toLowerCase() || '';

  const matchesSearch =
    !searchQuery ||
    customerName.toLowerCase().includes(search) ||
    invoiceNumber.toLowerCase().includes(search);

  const matchesClient =
    selectedClients.length === 0 ||
    selectedClients.some((client) => client.toLowerCase() === customerName.toLowerCase());

  console.log(
    `Invoice: ${invoice.invoiceNumber}, matchesSearch=${matchesSearch}, matchesClient=${matchesClient}`
  );

  return matchesSearch && matchesClient;
});



    return mappedInvoices;
  }, [invoices, generatedInvoices, tabValue, searchQuery, selectedClients]);

  useEffect(() => {
    if (tabValue === 'previous') {
      const selectedFortnight = fortnights.find((f) => f.value === fortnight);
      const payload = {
        user_whoRequested_id: parsedUserDetails.name || '',
        group_id: selectedProjectId || '',
        service_name_id: selectedServiceTitle.replace(' ', '_') || '',
        nested_component_id: 'Invoice',
        client_name:
          selectedClients.length > 0 ? selectedClients : parsedSelectedClient.label || '',
        filter_date: selectedFortnight
          ? selectedFortnight.startDate.toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0]
      };
      console.log('fetchInvoice payload:', payload);
      dispatch(fetchInvoice(payload));
    }
  }, [
    dispatch,
    tabValue,
    parsedUserDetails.name,
    parsedSelectedClient.label,
    selectedProjectId,
    selectedServiceTitle,
    selectedClients,
    fortnight,
    setFortnightDate
  ]);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: 'current' | 'previous') => {
      setTabValue(newValue);
      setSelectedInvoices([]);
      setSearchQuery('');
      dispatch(clearError());
      if (newValue === 'current') {
        dispatch(clearGeneratedInvoices());
      }
      setSearchParams({ tab: newValue });
    },
    [dispatch, setSearchParams]
  );

  const handleSelectInvoice = useCallback((invoiceId: string) => {
    setSelectedInvoices((prev) =>
      prev.includes(invoiceId) ? prev.filter((id) => id !== invoiceId) : [...prev, invoiceId]
    );
  }, []);

  const handleSelectAllInvoices = useCallback(() => {
    setSelectedInvoices((prev) =>
      prev.length === uiInvoices.length ? [] : uiInvoices.map((i) => i.invoiceNumber)
    );
  }, [uiInvoices]);

  const handleSelectClients = useCallback(
    (event: any) => {
      const value = event.target.value as string[];
      if (value.includes('all')) {
        setSelectedClients(
          selectedClients.length === clientsList.length
            ? []
            : clientsList.map((client) => client.label)
        );
      } else {
        setSelectedClients(value);
      }
    },
    [selectedClients, clientsList]
  );

  const handleResetFilters = useCallback(() => {
    setSelectedClients([]);
    const defaultFortnight = fortnights[10]?.value || '';
    setFortnight(defaultFortnight);
    setFortnightDate(defaultFortnight);
    setSearchQuery('');
    setSelectedInvoices([]);
    dispatch(clearInvoices());
    dispatch(clearGeneratedInvoices());
    if (tabValue === 'previous') {
      const payload = {
        user_whoRequested_id: parsedUserDetails.name || 'unknown',
        group_id: selectedProjectId || 'default',
        service_name_id: selectedServiceTitle.replace(' ', '_') || 'default',
        nested_component_id: 'Invoice',
        client_name: parsedSelectedClient.label || '',
        filter_date:
          fortnights[10]?.startDate.toISOString().split('T')[0] ||
          new Date().toISOString().split('T')[0]
      };
      console.log('fetchInvoice on refresh payload:', payload);
      dispatch(fetchInvoice(payload));
    }
  }, [
    dispatch,
    fortnights,
    parsedUserDetails.name,
    parsedSelectedClient.label,
    selectedProjectId,
    selectedServiceTitle,
    tabValue,
    setFortnightDate
  ]);

  const handleGenerateInvoices = useCallback(async () => {
    if (!fortnight) {
      setSnackbarMessage('Please select a fortnight.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (selectedClients.length === 0 && !parsedSelectedClient.label) {
      setSnackbarMessage('Please select at least one client.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const selectedFortnight = fortnights.find((f) => f.value === fortnight);
    if (!selectedFortnight) {
      setSnackbarMessage('Invalid fortnight selected.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const { startDate, endDate } = selectedFortnight;
    const formatDateTime = (date: Date) => new Date(date.setUTCHours(0, 0, 0, 0)).toISOString();

    const payload = {
      user_whoRequested_id: parsedUserDetails.name || 'unknown',
      group_id: selectedProjectId || 'default',
      service_name_id: selectedServiceTitle.replace(' ', '_') || 'default',
      nested_component_id: 'Invoice',
      client_name:
        selectedClients.length > 0 ? selectedClients : [parsedSelectedClient.label || ''],
      filter_date: formatDateTime(startDate),
      supports_delivered_from: formatDateTime(startDate),
      supports_delivered_to: formatDateTime(endDate)
    };
    console.log('generateInvoice payload:', payload);
    try {
      const result = await dispatch(generateInvoice(payload)).unwrap();
      console.log('generateInvoice result:', result);
      if (!result || result.length === 0) {
        setSnackbarMessage('No invoices generated. Please check the input parameters.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(`Generated ${result.length} invoices successfully.`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('generateInvoice failed:', error);
      setSnackbarMessage('Failed to generate invoices. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [
    dispatch,
    fortnight,
    selectedClients,
    parsedSelectedClient.label,
    selectedProjectId,
    selectedServiceTitle,
    fortnights
  ]);

  const handleDelete = useCallback(
    (invoice: Invoice) => {
      if (!invoice.id && !invoice.invoice_number) {
        console.error('Invoice ID is missing');
        setSnackbarMessage('Cannot delete invoice: Missing ID.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      const invoiceId = invoice.id || invoice.invoice_number;
      const selectedFortnight = fortnights.find((f) => f.value === fortnight);
      const payload = {
        user_whoRequested_id: parsedUserDetails.name || 'unknown',
        group_id: selectedProjectId || 'default',
        service_name_id: selectedServiceTitle.replace(' ', '_') || 'default',
        nested_component_id: 'Invoice',
        invoice_id: invoiceId,
        client_name: invoice.customer_name || parsedSelectedClient.label || '',
        client_key:invoice?.client_key,
        filter_date: selectedFortnight
          ? selectedFortnight.startDate.toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0]
      };
      dispatch(deleteInvoice(payload));
      onDelete(invoice);
    },
    [
      dispatch,
      onDelete,
      parsedUserDetails.name,
      parsedSelectedClient.label,
      selectedProjectId,
      selectedServiceTitle,
      fortnight,
      fortnights
    ]
  );

  const handlePrint = useCallback(
    (invoice: Invoice) => {
      if (!invoice.id && !invoice.invoice_number) {
        console.error('Invalid invoice for printing:', invoice);
        setSnackbarMessage('Cannot print invoice: Missing ID.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      console.log('Printing invoice:', invoice);
      onPrint(invoice);
    },
    [onPrint]
  );

  const handleWorkloadReport = useCallback((invoice: Invoice) => {
    console.log('handleWorkloadReport', invoice);
    setSelectedInvoice(invoice);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedInvoice(null);
  }, []);

  const handleExport = useCallback(
    async (format: 'excel' | 'csv') => {
      if (selectedInvoices.length === 0) {
        setSnackbarMessage('Please select at least one invoice to export.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      console.log('generatedInvoices in export', generatedInvoices);
      try {
        const exportData: ExportInvoiceRow[] = [];
        const sourceInvoices = tabValue === 'current' ? generatedInvoices : invoices;
        const selectedFortnight = fortnights.find((f) => f.value === fortnight);

        for (const invoiceNumber of selectedInvoices) {
          const uiInvoice = uiInvoices.find((inv) => inv.invoiceNumber === invoiceNumber);
          if (!uiInvoice) continue;

          const originalInvoice = sourceInvoices.find(
            (inv: any) => inv.invoice_number === invoiceNumber
          );
          if (!originalInvoice) continue;
          console.log('uiInvoice', uiInvoice, sourceInvoices);
          const payload = {
            user_whoRequested_id: parsedUserDetails.name || 'unknown',
            group_id: selectedProjectId || 'default',
            service_name_id: selectedServiceTitle.replace(' ', '_') || 'default',
            nested_component_id: 'Invoice',
            invoice_id: uiInvoice.id || uiInvoice.invoiceNumber,
            client_name: uiInvoice.customerName || '',
            client_key: originalInvoice?.client_key || '',
            filter_date: selectedFortnight
              ? selectedFortnight.startDate.toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0]
          };

          const response = (await dispatch(fetchSingleInvoice(payload)).unwrap()) as Invoice;
          const invoice = response && response.invoice_number ? response : null;
          if (!invoice) continue;

          if (invoice.shifts && invoice.shifts.length > 0) {
            invoice.shifts.forEach((shift: Shift) => {
              const participantName = uiInvoice.customerName?.split(' ')[0] || 'Participant';
              const deliveredFromDate = new Date(shift.supports_delivered_from || '');
              const formattedDate = `${String(deliveredFromDate.getDate()).padStart(
                2,
                '0'
              )}${String(deliveredFromDate.getMonth() + 1).padStart(
                2,
                '0'
              )}${deliveredFromDate.getFullYear()}`;

              exportData.push({
                RegistrationNumber: invoice.registration_number || '',
                NDISNumber: invoice.ndis_number || '',
                SupportsDeliveredFrom: shift.supports_delivered_from || '',
                SupportsDeliveredTo: shift.supports_delivered_to || '',
                SupportNumber: shift.support_number || '',
                ClaimReference: shift?.claimreference || '',
                Quantity: '',
                Hours: Math.ceil(Number(shift.Planned_Hours || 0)).toString(),
                UnitPrice: shift.unit_price?.toString() || '0',
                GSTCode: invoice.gst_code || '',
                AuthorisedBy: invoice.authorised_by || '',
                ParticipantApproved: invoice.participant_approved ? 'Y' : 'N',
                InKindFundingProgram: invoice.in_kind_funding_program || '',
                ClaimType: invoice.claim_type || '',
                CancellationReason: '',
                'ABN of Support Provider': invoice.abn || '',
                InvoiceNumber: invoice.invoice_number || ''
              });
            });
          } else {
            exportData.push({
              RegistrationNumber: invoice.registration_number || '',
              NDISNumber: invoice.ndis_number || '',
              SupportsDeliveredFrom: '',
              SupportsDeliveredTo: '',
              SupportNumber: '',
              ClaimReference: invoice.client_reference || '',
              Quantity: '',
              Hours: '0',
              UnitPrice: '0',
              GSTCode: invoice.gst_code || '',
              AuthorisedBy: invoice.authorised_by || '',
              ParticipantApproved: invoice.participant_approved ? 'Y' : 'N',
              InKindFundingProgram: invoice.in_kind_funding_program || '',
              ClaimType: invoice.claim_type || '',
              CancellationReason: '',
              'ABN of Support Provider': invoice.abn || '',
              InvoiceNumber: invoice.invoice_number || ''
            });
          }
        }

        if (exportData.length === 0) {
          setSnackbarMessage('No valid data available to export.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
        }

        const headers = [
          'RegistrationNumber',
          'NDISNumber',
          'SupportsDeliveredFrom',
          'SupportsDeliveredTo',
          'SupportNumber',
          'ClaimReference',
          'Quantity',
          'Hours',
          'UnitPrice',
          'GSTCode',
          'AuthorisedBy',
          'ParticipantApproved',
          'InKindFundingProgram',
          'ClaimType',
          'CancellationReason',
          'ABN of Support Provider'
        ];

        const wsData = [headers];
        exportData.forEach((row) => {
          wsData.push([
            row.RegistrationNumber,
            row.NDISNumber,
            row.SupportsDeliveredFrom,
            row.SupportsDeliveredTo,
            row.SupportNumber,
            row.ClaimReference,
            row.Quantity,
            row.Hours,
            row.UnitPrice,
            row.GSTCode,
            row.AuthorisedBy,
            row.ParticipantApproved,
            row.InKindFundingProgram,
            row.ClaimType,
            row.CancellationReason,
            row['ABN of Support Provider']
          ]);
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, 'Invoices');

        const timestamp = new Date().toISOString().split('T')[0];
        const fileName = `invoice_details_${timestamp}.${format === 'excel' ? 'xlsx' : 'csv'}`;

        XLSX.writeFile(wb, fileName, { bookType: format === 'excel' ? 'xlsx' : 'csv' });
        setSnackbarMessage(`Invoices exported to ${format.toUpperCase()} successfully.`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (err) {
        console.error('Export failed:', err);
        setSnackbarMessage('Failed to export invoices. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    },
    [
      dispatch,
      selectedInvoices,
      tabValue,
      generatedInvoices,
      invoices,
      uiInvoices,
      parsedUserDetails.name,
      selectedProjectId,
      selectedServiceTitle,
      fortnight,
      fortnights
    ]
  );

  const formatDate = useCallback((dateString: string): string => {
    return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
  }, []);

  const getListItemContent = useCallback(
    (invoice: TableInvoice): ReactNode => {
      if (isMobile) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <ListItemAvatar sx={{ minWidth: 48 }}>
              <Avatar src={invoice.avatar} sx={{ width: 36, height: 36 }} alt="Customer avatar">
                {!invoice.avatar && <PersonIcon />}
              </Avatar>
            </ListItemAvatar>
            <Box sx={{ flex: 1, pr: 1 }}>
              <Typography variant="subtitle2">{invoice.customerName}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <ReceiptIcon color="action" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  {invoice.invoiceNumber}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayIcon color="action" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  {formatDate(invoice.date)}
                </Typography>
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                <Chip
                  icon={<HoursIcon />}
                  label={`${(invoice.totalHours || 0).toFixed(2)} hrs`}
                  size="small"
                  aria-label={`Total hours: ${(invoice.totalHours || 0).toFixed(2)}`}
                />
                <Chip
                  icon={<MoneyIcon />}
                  label={`$${(invoice.totalAmount || 0).toFixed(2)}`}
                  color="primary"
                  size="small"
                  aria-label={`Total amount: $${(invoice.totalAmount || 0).toFixed(2)}`}
                />
              </Box>
            </Box>
          </Box>
        );
      }

      return (
        <>
          <ListItemText
            primary={
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar src={invoice.avatar} sx={{ width: 32, height: 32 }} alt="Customer avatar">
                  {!invoice.avatar && <PersonIcon />}
                </Avatar>
                <Typography variant="body2">{invoice.customerName}</Typography>
              </Stack>
            }
            sx={{ flex: '1 1 25%' }}
          />
          <ListItemText
            primary={
              <Stack direction="row" alignItems="center" spacing={1}>
                <ReceiptIcon color="action" fontSize="small" />
                <Typography>{invoice.invoiceNumber}</Typography>
              </Stack>
            }
            sx={{ flex: '1 1 20%' }}
          />
          <ListItemText
            primary={
              <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
                <CalendarTodayIcon color="action" fontSize="small" />
                <Typography>{formatDate(invoice.date)}</Typography>
              </Stack>
            }
            sx={{ flex: '1 1 20%' }}
          />
          <ListItemText
            primary={
              <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
                <HoursIcon color="action" fontSize="small" />
                <Typography>{(invoice.totalHours || 0).toFixed(2)} hrs</Typography>
              </Stack>
            }
            sx={{ flex: '1 1 15%' }}
          />
          <ListItemText
            primary={
              <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
                <MoneyIcon color="primary" fontSize="small" />
                <Typography fontWeight="medium">
                  ${(invoice.totalAmount || 0).toFixed(2)}
                </Typography>
              </Stack>
            }
            sx={{ flex: '1 1 15%' }}
          />
        </>
      );
    },
    [isMobile, formatDate]
  );

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const handleFortnightChange = useCallback(
    (value: string) => {
      setFortnight(value);
      setFortnightDate(value);
    },
    [setFortnightDate]
  );

  console.log(
    "uiInvoices.length === 0 && tabValue === 'current'",
    uiInvoices.length === 0 && tabValue === 'current',
    uiInvoices,
    tabValue
  );

  return (
    <Box
      sx={{
        p: isMobile ? 1 : 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 1 : 2
      }}
      role="region"
      aria-label="Invoice Management Section"
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}
          component="h1"
        >
          <ReceiptIcon color="primary" sx={{ mr: 1, fontSize: 24 }} aria-hidden="true" />
          Invoices
        </Typography>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            '& .MuiTabs-indicator': { backgroundColor: theme.palette.primary.main },
            minWidth: isMobile ? '100%' : 300
          }}
          aria-label="Invoice Tabs"
        >
          <Tab
            label="Current"
            value="current"
            icon={<CalendarTodayIcon fontSize="small" />}
            iconPosition="start"
            aria-label="View Current Invoices"
            sx={{ fontSize: isMobile ? 12 : 14, minHeight: 48 }}
          />
          <Tab
            label="Previous"
            value="previous"
            icon={<CalendarTodayIcon fontSize="small" />}
            iconPosition="start"
            aria-label="View Previous Invoices"
            sx={{ fontSize: isMobile ? 12 : 14, minHeight: 48 }}
          />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => dispatch(clearError())} role="alert">
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress aria-label="Loading invoices" />
        </Box>
      )}

      {!loading && (
        <Paper
          elevation={1}
          sx={{
            p: isMobile ? 1 : 2,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 1 : 2,
            background:
              theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50]
          }}
        >
          <FormControl
            size="small"
            sx={{ width: isMobile ? '100%' : 200 }}
            aria-label="Select Clients"
          >
            <InputLabel id="clients-select-label">Clients</InputLabel>
            <Select
              labelId="clients-select-label"
              multiple
              value={selectedClients}
              onChange={handleSelectClients}
              label="Clients"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              <MenuItem value="all">
                <Checkbox
                  checked={selectedClients.length === clientsList.length && clientsList.length > 0}
                  indeterminate={
                    selectedClients.length > 0 && selectedClients.length < clientsList.length
                  }
                  aria-label="Select All Clients"
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              {clientsList.length > 0 ? (
                clientsList.map((client) => (
                  <MenuItem key={client.id} value={client.label}>
                    <Checkbox checked={selectedClients.includes(client.label)} />
                    <ListItemText primary={client.label} />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <ListItemText primary="No clients available" />
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{ width: isMobile ? '100%' : 200 }}
            aria-label="Select Fortnight"
          >
            <InputLabel id="fortnight-select-label">Fortnight</InputLabel>
            <Select
              labelId="fortnight-select-label"
              value={fortnight}
              onChange={(e) => handleFortnightChange(e.target.value as string)}
              label="Fortnight"
            >
              <MenuItem value="">
                <em>Select Fortnight</em>
              </MenuItem>
              {fortnights.map(({ value }) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search invoices..."
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
            }}
            sx={{ width: isMobile ? '100%' : 200 }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search Invoices"
          />

          {tabValue === 'current' && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateInvoices}
              disabled={!fortnight || loading}
              sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 1 : 0 }}
              startIcon={<AddIcon />}
              aria-label="Generate Invoices"
            >
              Generate
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={handleResetFilters}
            size="small"
            sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 1 : 0 }}
            startIcon={<RefreshIcon />}
            aria-label="Refresh Filters and Data"
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={onCreate}
            sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 1 : 0 }}
            startIcon={<AddIcon />}
            aria-label="Create New Invoice"
          >
            Create
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleExport('excel')}
            disabled={selectedInvoices.length === 0 || loading}
            sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 1 : 0 }}
            startIcon={<FileDownloadIcon />}
            aria-label="Export Invoices to Excel"
          >
            Export Excel
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleExport('csv')}
            disabled={selectedInvoices.length === 0 || loading}
            sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 1 : 0 }}
            startIcon={<FileDownloadIcon />}
            aria-label="Export Invoices to CSV"
          >
            Export CSV
          </Button>
        </Paper>
      )}

      {!loading && (
        <Paper
          elevation={1}
          sx={{
            flex: 1,
            overflow: 'auto',
            background: theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#fff',
            p: isMobile ? 1 : 0
          }}
          role="grid"
          aria-label="Invoices List"
        >
          {isMobile ? (
            // Mobile Card View
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {uiInvoices.length === 0 && tabValue === 'current' ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                    textAlign: 'center',
                    p: 3,
                    bgcolor:
                      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    borderRadius: 2
                  }}
                >
                  <ReceiptIcon
                    color="disabled"
                    sx={{
                      fontSize: 48,
                      mb: 2,
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.grey[500]
                          : theme.palette.grey[400]
                    }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Invoices Generated
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select clients and fortnight, then click 'Generate'
                  </Typography>
                </Box>
              ) : uiInvoices.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                    textAlign: 'center',
                    p: 3,
                    bgcolor:
                      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    borderRadius: 2
                  }}
                >
                  <SearchIcon
                    color="disabled"
                    sx={{
                      fontSize: 48,
                      mb: 2,
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.grey[500]
                          : theme.palette.grey[400]
                    }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Invoices Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting filters or fetching data
                  </Typography>
                </Box>
              ) : (
                uiInvoices.map((uiInvoice: TableInvoice) => {
                  const originalInvoice = (
                    tabValue === 'current' ? generatedInvoices : invoices
                  ).find((inv: Invoice) => inv.invoice_number === uiInvoice.invoiceNumber);

                  if (!originalInvoice) {
                    return (
                      <Paper
                        key={uiInvoice.id}
                        elevation={0}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: theme.palette.error.main,
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(244,67,54,0.1)'
                              : 'rgba(244,67,54,0.05)'
                        }}
                      >
                        <Typography color="error">
                          Error: Invoice data incomplete (ID: {uiInvoice.invoiceNumber})
                        </Typography>
                      </Paper>
                    );
                  }

                  return (
                    <Paper
                      key={uiInvoice.id}
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                        position: 'relative',
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.grey[800]
                            : theme.palette.background.paper,
                        '&:hover': {
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.grey[700]
                              : theme.palette.grey[50]
                        }
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedInvoices.includes(uiInvoice.invoiceNumber)}
                          onChange={() => handleSelectInvoice(uiInvoice.invoiceNumber)}
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? theme.palette.grey[400]
                                : theme.palette.grey[600],
                            '&.Mui-checked': {
                              color: theme.palette.primary.main
                            }
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 1.5,
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText
                          }}
                        >
                          {uiInvoice.customerName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={500}
                            noWrap
                            color={
                              theme.palette.mode === 'dark'
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900]
                            }
                          >
                            {uiInvoice.customerName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            #{uiInvoice.invoiceNumber}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: 1.5,
                          mb: 2
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Date
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {formatDate(uiInvoice.date)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Hours
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {uiInvoice.totalHours.toFixed(2)} hrs
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Amount
                          </Typography>
                          <Typography variant="body2" fontWeight={500} color="text.primary">
                            ${uiInvoice.totalAmount.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 1
                        }}
                      >
                        <Tooltip title="View">
                          <IconButton
                            onClick={() => onView(originalInvoice)}
                            size="small"
                            sx={{
                              color: theme.palette.primary.main,
                              flex: 1
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => onEdit(originalInvoice)}
                            size="small"
                            sx={{
                              color: theme.palette.info.main,
                              flex: 1
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                          <IconButton
                            onClick={() => handlePrint(originalInvoice)}
                            size="small"
                            sx={{
                              color: theme.palette.secondary.main,
                              flex: 1
                            }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Workload Report">
                          <IconButton
                            onClick={() => handleWorkloadReport(originalInvoice)}
                            size="small"
                            sx={{
                              color: theme.palette.warning.main,
                              flex: 1
                            }}
                          >
                            <AssignmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDelete(originalInvoice)}
                            size="small"
                            sx={{
                              color: theme.palette.error.main,
                              flex: 1
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                  );
                })
              )}
            </Box>
          ) : (
            // Desktop Table View
            <List dense={false}>
              {!isMobile && (
                <ListItem
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f5f5f5',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    py: 1
                  }}
                  role="row"
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox
                      edge="start"
                      checked={
                        selectedInvoices.length === uiInvoices.length && uiInvoices.length > 0
                      }
                      indeterminate={
                        selectedInvoices.length > 0 && selectedInvoices.length < uiInvoices.length
                      }
                      onChange={handleSelectAllInvoices}
                      inputProps={{ 'aria-label': 'Select All Invoices' }}
                      sx={{
                        color:
                          theme.palette.mode === 'dark'
                            ? theme.palette.grey[400]
                            : theme.palette.grey[600],
                        '&.Mui-checked': {
                          color: theme.palette.primary.main
                        }
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="subtitle2">Customer</Typography>}
                    sx={{ flex: '1 1 25%' }}
                    role="columnheader"
                  />
                  <ListItemText
                    primary={<Typography variant="subtitle2">Invoice #</Typography>}
                    sx={{ flex: '1 1 20%' }}
                    role="columnheader"
                  />
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" align="right">
                        Date
                      </Typography>
                    }
                    sx={{ flex: '1 1 20%' }}
                    role="columnheader"
                  />
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" align="right">
                        Hours
                      </Typography>
                    }
                    sx={{ flex: '1 1 15%' }}
                    role="columnheader"
                  />
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" align="right">
                        Amount
                      </Typography>
                    }
                    sx={{ flex: '1 1 15%' }}
                    role="columnheader"
                  />
                  <Box sx={{ width: 200 }} role="columnheader" />
                </ListItem>
              )}
              {uiInvoices.length === 0 && tabValue === 'current' ? (
                <ListItem role="row">
                  <ListItemText
                    primary={
                      <Typography color="text.secondary" textAlign="center" py={2}>
                        No invoices generated. Select clients and fortnight, then click 'Generate'.
                      </Typography>
                    }
                    role="cell"
                  />
                </ListItem>
              ) : uiInvoices.length === 0 ? (
                <ListItem role="row">
                  <ListItemText
                    primary={
                      <Typography color="text.secondary" textAlign="center" py={2}>
                        No invoices found. Try adjusting filters or fetching data.
                      </Typography>
                    }
                    role="cell"
                  />
                </ListItem>
              ) : (
                uiInvoices.map((uiInvoice: TableInvoice) => {
                  console.log('tabValue', tabValue);
                  const originalInvoice = (
                    tabValue === 'current' ? generatedInvoices : invoices
                  ).find((inv: Invoice) => inv.invoice_number === uiInvoice.invoiceNumber);

                  if (!originalInvoice) {
                    return (
                      <ListItem key={uiInvoice.id} role="row">
                        <ListItemText
                          primary={
                            <Typography color="error">
                              Error: Invoice data incomplete (ID: {uiInvoice.invoiceNumber})
                            </Typography>
                          }
                          role="cell"
                        />
                      </ListItem>
                    );
                  }

                  return (
                    <ListItem
                      key={uiInvoice.id}
                      sx={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        py: 1,
                        '&:hover': {
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.grey[800]
                              : theme.palette.grey[50]
                        }
                      }}
                      role="row"
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Checkbox
                          edge="start"
                          checked={selectedInvoices.includes(uiInvoice.invoiceNumber)}
                          onChange={() => handleSelectInvoice(uiInvoice.invoiceNumber)}
                          inputProps={{
                            'aria-label': `Select invoice ${uiInvoice.invoiceNumber}`
                          }}
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? theme.palette.grey[400]
                                : theme.palette.grey[600],
                            '&.Mui-checked': {
                              color: theme.palette.primary.main
                            }
                          }}
                        />
                      </ListItemIcon>
                      {getListItemContent(uiInvoice)}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View">
                          <IconButton
                            onClick={() => onView(originalInvoice)}
                            size="small"
                            sx={{
                              color: theme.palette.primary.main
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => onEdit(originalInvoice)}
                            size="small"
                            sx={{
                              color: theme.palette.success.main
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                          <IconButton
                            onClick={() => handlePrint(originalInvoice)}
                            size="small"
                            sx={{
                              color: theme.palette.secondary.main
                            }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Workload Report">
                          <IconButton
                            onClick={() => handleWorkloadReport(originalInvoice)}
                            size="small"
                            sx={{
                              color: theme.palette.primary.dark
                            }}
                          >
                            <AssignmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDelete(originalInvoice)}
                            size="small"
                            sx={{
                              color: theme.palette.error.main
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  );
                })
              )}
            </List>
          )}
        </Paper>
      )}

      {!loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#f5f5f5'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {uiInvoices.length} invoices
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ChevronLeftIcon />}
              disabled
              aria-label="Previous Page"
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              endIcon={<ChevronRightIcon />}
              disabled={uiInvoices.length <= 10}
              aria-label="Next Page"
            >
              Next
            </Button>
          </Stack>
        </Box>
      )}

      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        aria-labelledby="workload-report-modal-title"
        aria-describedby="workload-report-modal-description"
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: isMobile ? '95%' : '80%',
              maxWidth: 800,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 0.5,
              borderRadius: 2,
              maxHeight: '90vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {selectedInvoice && (
              <WorkloadReport
                invoice={selectedInvoice}
                onEdit={onEdit}
                onBack={handleCloseModal}
                isSmallScreen={isSmallScreen}
                fortnightDate={fortnight}
              />
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 2,
                width: '100%'
              }}
            >
              <Button
                onClick={handleCloseModal}
                variant="outlined"
                aria-label="Close Workload Report"
                sx={{
                  alignSelf: 'flex-end',
                  ml: 'auto'
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceList;
